import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Nav, Footer, DisclaimerBar } from '../components/Chrome';
import { api } from '../api';
import ReportQA from '../components/ReportQA';

const verdictLabel = { proceed: 'PROCEED', negotiate: 'NEGOTIATE', hold: 'HOLD', reject: 'REJECT' };

const fmtMoney = (n) => n == null ? '—' : `$${Number(n).toLocaleString()}`;

function VerdictBadge({ v, conf }) {
  return (
    <div className="flex items-center gap-4">
      <span className={`verdict-badge verdict-${v}`}><span className="opacity-60">▸</span>{verdictLabel[v] || v?.toUpperCase()}</span>
      {conf != null && <span className="mono text-xs text-white/60">confidence · <span className="text-white">{conf}</span>/100</span>}
    </div>
  );
}

function RiskLevelBadge({ level }) {
  const map = {
    low: 'text-emerald-400 border-emerald-400/40',
    moderate: 'text-amber-400 border-amber-400/40',
    high: 'text-red-400 border-red-400/40',
    unknown: 'text-white/40 border-white/20',
  };
  return <span className={`mono text-[10px] uppercase tracking-widest2 border px-2 py-0.5 ${map[level] || map.unknown}`}>{level}</span>;
}

function AVMBar({ low, median, high }) {
  const span = Math.max(1, high - low);
  const medianPct = ((median - low) / span) * 100;
  return (
    <div>
      <div className="relative h-8 border hairline bg-obsidian">
        <div className="absolute inset-y-0 left-0 right-0 bg-gradient-to-r from-gold/10 via-gold/30 to-gold/10" />
        <div className="absolute inset-y-0 w-0.5 bg-gold" style={{ left: `${medianPct}%` }} />
        <div className="absolute inset-y-0 left-0 w-1 bg-white/40" />
        <div className="absolute inset-y-0 right-0 w-1 bg-white/40" />
      </div>
      <div className="mt-2 flex justify-between mono text-xs">
        <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Low</div><div className="text-white">{fmtMoney(low)}</div></div>
        <div className="text-center"><div className="text-gold/60 text-[10px] uppercase tracking-widest2">Median</div><div className="text-gold">{fmtMoney(median)}</div></div>
        <div className="text-right"><div className="text-white/40 text-[10px] uppercase tracking-widest2">High</div><div className="text-white">{fmtMoney(high)}</div></div>
      </div>
    </div>
  );
}

function MdcCell({ label, value, available, highlight }) {
  const displayVal = value === null || value === undefined || value === '' ? '—' : String(value);
  return (
    <div className={`border hairline bg-obsidian p-3 ${highlight ? 'border-amber-400/60' : ''}`}>
      <div className="mono text-[9px] uppercase tracking-widest2 text-white/40">{label}</div>
      <div className={`mono text-sm mt-1 truncate ${highlight ? 'text-amber-400' : available ? 'text-white' : 'text-white/30'}`} title={displayVal}>{displayVal}</div>
    </div>
  );
}

function SpatialForensicsCard({ spatial, lang }) {
  if (!spatial || !spatial.ok) return null;
  const t = (en, es) => (lang === 'es' ? es : en);
  const geo = spatial.geocode || {};
  const elev = spatial.elevation || {};
  const poi = spatial.poi || {};
  const noaa = spatial.noaa || {};
  const mdc = spatial.miami_dade || {};
  const bwd = spatial.broward || {};
  const pbc = spatial.palm_beach || {};
  const StatusPill = ({ ok, label }) => (
    <span className={`mono text-[10px] uppercase tracking-widest2 px-1.5 py-0.5 border ${ok ? 'text-emerald-400 border-emerald-400/40' : 'text-white/40 border-white/20'}`}>{label}</span>
  );
  // Miami-Dade indicator lookup
  const mdcById = Object.fromEntries((mdc.indicators || []).map(i => [i.id, i]));
  const mdcVal = (id, path) => {
    const ind = mdcById[id];
    if (!ind?.available) return null;
    const s = ind.summary || {};
    return path.split('.').reduce((acc, k) => acc?.[k], s) ?? null;
  };
  return (
    <div className="lg:col-span-12 border hairline bg-surface1 p-6" data-testid="spatial-forensics">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ SPATIAL FORENSICS</div>
        <div className="mono text-[10px] text-white/50">
          {geo.lat != null && geo.lon != null && (
            <>coords · {Number(geo.lat).toFixed(4)}, {Number(geo.lon).toFixed(4)} · {geo.source || 'osm_nominatim'}</>
          )}
        </div>
      </div>
      <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Elevation / water table */}
        <div className="border hairline bg-obsidian p-4">
          <div className="flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/50">{t('WATER TABLE / ELEVATION','NIVEL FREÁTICO / ELEVACIÓN')}</div>
            <StatusPill ok={elev.available} label={elev.available ? 'live' : 'n/a'} />
          </div>
          {elev.available ? (
            <>
              <div className="serif text-3xl mt-3 text-gold">{elev.elevation_ft} <span className="mono text-xs text-white/50">ft</span></div>
              <div className="mono text-[10px] uppercase tracking-widest2 mt-2 text-white/60">band · <span className={elev.band === 'very_low' ? 'text-red-400' : elev.band === 'low' ? 'text-amber-400' : elev.band === 'moderate' ? 'text-white' : 'text-emerald-400'}>{elev.band}</span></div>
              <div className="mono text-[10px] text-white/40 mt-2">source · usgs_epqs_v1</div>
            </>
          ) : (
            <div className="mono text-xs text-white/40 mt-3">{elev.reason || 'not_available'}</div>
          )}
        </div>

        {/* NOAA climate */}
        <div className="border hairline bg-obsidian p-4">
          <div className="flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/50">{t('CLIMATE / ACTIVE ALERTS','CLIMA / ALERTAS ACTIVAS')}</div>
            <StatusPill ok={noaa.available} label={noaa.available ? 'live' : 'n/a'} />
          </div>
          {noaa.available ? (
            <>
              <div className="serif text-3xl mt-3 text-gold">{noaa.active_alerts_count}</div>
              <div className="mono text-[10px] uppercase tracking-widest2 mt-2 text-white/60">nws office · {noaa.office} · zone {noaa.zone_id}</div>
              {(noaa.active_alerts || []).slice(0,2).map((a, i) => (
                <div key={`alert-${a.event}-${i}`} className="mt-2 mono text-[10px] text-amber-400">⚠ {a.event} · {a.severity}</div>
              ))}
              <div className="mono text-[10px] text-white/40 mt-2">source · noaa_weathergov</div>
            </>
          ) : (
            <div className="mono text-xs text-white/40 mt-3">{noaa.reason || 'not_available'}</div>
          )}
        </div>

        {/* Neighborhood infrastructure */}
        <div className="border hairline bg-obsidian p-4">
          <div className="flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/50">{t('INFRASTRUCTURE (1km)','INFRAESTRUCTURA (1km)')}</div>
            <StatusPill ok={poi.available} label={poi.available ? 'live' : 'n/a'} />
          </div>
          {poi.available ? (
            <>
              <div className="mt-3 grid grid-cols-2 gap-x-2 gap-y-1 mono text-[11px]">
                <div className="text-white/50">schools</div><div className="text-gold text-right">{poi.counts.schools}</div>
                <div className="text-white/50">hospitals</div><div className="text-gold text-right">{poi.counts.hospitals}</div>
                <div className="text-white/50">supermarkets</div><div className="text-white text-right">{poi.counts.supermarkets}</div>
                <div className="text-white/50">worship</div><div className="text-white text-right">{poi.counts.places_of_worship}</div>
                <div className="text-white/50">signals</div><div className="text-white text-right">{poi.counts.traffic_signals}</div>
                <div className="text-white/50">main roads</div><div className="text-white text-right">{poi.counts.main_roads_nearby}</div>
                <div className="text-white/50">construction</div><div className={`text-right ${poi.counts.construction_sites_visible > 0 ? 'text-amber-400' : 'text-white'}`}>{poi.counts.construction_sites_visible}</div>
              </div>
              <div className="mono text-[10px] text-white/40 mt-2">source · osm_overpass</div>
            </>
          ) : (
            <div className="mt-3">
              <div className="mono text-xs text-white/60">{t('Public POI data source temporarily unavailable.','Fuente pública de POI temporalmente no disponible.')}</div>
              <div className="mono text-[10px] text-white/30 mt-2">debug · {poi.reason || 'not_available'}</div>
            </div>
          )}
        </div>

        {/* Integration-pending block: transparency about what we DON'T have */}
        <div className="border hairline bg-obsidian p-4">
          <div className="flex items-center justify-between">
            <div className="mono text-[10px] uppercase tracking-widest2 text-white/50">{t('AWAITING INTEGRATION','INTEGRACIÓN PENDIENTE')}</div>
            <StatusPill ok={false} label="n/a" />
          </div>
          <ul className="mt-3 space-y-1.5 mono text-[11px] text-white/60">
            <li>• {t('Future zoning maps','Zonificación futura')}</li>
            <li>• {t('HOA / pet policies','HOA / mascotas')}</li>
            <li>• {t('Live traffic congestion','Congestión vial en vivo')}</li>
            <li>• {t('Projected municipal projects','Proyectos municipales proyectados')}</li>
          </ul>
          <div className="mono text-[10px] text-white/30 mt-3">no public API · not fabricated</div>
        </div>
      </div>

      {/* Miami-Dade ArcGIS enrichment (25 curated layers from services.arcgis.com/8Pc9XBTAsYuxx9Ny) */}
      {mdc.ok && (
        <div className="mt-6 border-t hairline pt-6" data-testid="mdc-arcgis-panel">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ MIAMI-DADE ARCGIS · {mdc.layers_queried || 25} curated layers</div>
            <div className="mono text-[10px] text-white/50">
              {mdc.applicable ? `applicable · ${mdc.counts?.available}/${mdc.counts?.total} responded` : t('outside miami-dade — layers may be empty','fuera de miami-dade — capas pueden estar vacías')} · adapter v{mdc.adapter_version}
            </div>
          </div>

          {/* Group 1: Land / Legal */}
          <div className="mt-5">
            <div className="mono text-[9px] uppercase tracking-widest2 text-white/40 mb-2">— {t('LAND · LEGAL','SUELO · LEGAL')}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mono text-[11px]">
              <MdcCell label={t('Parcel folio','Folio parcela')} value={mdcVal('parcel','folio')} available={!!mdcById.parcel?.available} />
              <MdcCell label={t('Current land use','Uso actual')} value={mdcVal('current_land_use','use_code')} available={!!mdcById.current_land_use?.available} />
              <MdcCell label={t('Future land use','Uso futuro')} value={mdcVal('future_land_use','designation')} available={!!mdcById.future_land_use?.available} />
              <MdcCell label={t('HOA','HOA')} value={mdcVal('hoa_boundary','in_hoa') ? t('inside','dentro') : t('none','ninguna')} available={!!mdcById.hoa_boundary?.available} highlight={mdcVal('hoa_boundary','in_hoa')} />
              <MdcCell label={t('Community council','Consejo comunitario')} value={mdcVal('community_council','council')} available={!!mdcById.community_council?.available} />
            </div>
          </div>

          {/* Group 2: Flood / Coastal / Climate */}
          <div className="mt-5">
            <div className="mono text-[9px] uppercase tracking-widest2 text-white/40 mb-2">— {t('FLOOD · COASTAL · CLIMATE','INUNDACIÓN · COSTA · CLIMA')}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mono text-[11px]">
              <MdcCell label={t('Prelim. FEMA zone','Zona FEMA prelim.')} value={mdcVal('preliminary_flood_zone','prelim_zone')} available={!!mdcById.preliminary_flood_zone?.available} highlight={['A','AE','V','VE'].includes(mdcVal('preliminary_flood_zone','prelim_zone'))} />
              <MdcCell label={t('Coastal A zone','Zona costera A')} value={mdcVal('coastal_a_zone','in_coastal_a_zone') ? t('yes','sí') : t('no','no')} available={!!mdcById.coastal_a_zone?.available} highlight={mdcVal('coastal_a_zone','in_coastal_a_zone')} />
              <MdcCell label={t('Saltwater intrusion','Intrusión salina')} value={mdcVal('saltwater_intrusion','inside_intrusion_zone') ? t('inside','dentro') : t('outside','fuera')} available={!!mdcById.saltwater_intrusion?.available} highlight={mdcVal('saltwater_intrusion','inside_intrusion_zone')} />
              <MdcCell label={t('Storm surge planning','Marejada planeada')} value={mdcVal('storm_surge_planning','in_surge_zone') ? t('inside','dentro') : t('outside','fuera')} available={!!mdcById.storm_surge_planning?.available} highlight={mdcVal('storm_surge_planning','in_surge_zone')} />
              <MdcCell label={t('Hurricane evac zone','Zona evac. huracán')} value={mdcVal('hurricane_evac_zone','zone')} available={!!mdcById.hurricane_evac_zone?.available} highlight={['A','B'].includes(mdcVal('hurricane_evac_zone','zone'))} />
              <MdcCell label={t('Heat vulnerability','Vulnerab. calor')} value={mdcVal('heat_vulnerability','vulnerability')} available={!!mdcById.heat_vulnerability?.available} />
              <MdcCell label={t('Tree canopy % (ZIP)','% Copa arbórea (ZIP)')} value={mdcVal('tree_canopy','canopy_pct_2020')} available={!!mdcById.tree_canopy?.available} />
              <MdcCell label={t('Septic / well','Séptico / pozo')} value={mdcVal('septic_or_well','on_septic_or_well') ? t('yes','sí') : t('no','no')} available={!!mdcById.septic_or_well?.available} highlight={mdcVal('septic_or_well','on_septic_or_well')} />
            </div>
          </div>

          {/* Group 3: Emergency response */}
          <div className="mt-5">
            <div className="mono text-[9px] uppercase tracking-widest2 text-white/40 mb-2">— {t('EMERGENCY RESPONSE','EMERGENCIA')}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mono text-[11px]">
              <MdcCell label={t('Fire stations (2km)','Bomberos (2km)')} value={mdcVal('fire_stations_2km','count_2km')} available={!!mdcById.fire_stations_2km?.available} highlight={(mdcVal('fire_stations_2km','count_2km') || 0) === 0} />
              <MdcCell label={t('Hospitals (3km)','Hospitales (3km)')} value={mdcVal('hospitals_3km','count_3km')} available={!!mdcById.hospitals_3km?.available} />
              <MdcCell label={t('Evac shelters (5km)','Refugios evac. (5km)')} value={mdcVal('evac_shelters_5km','count_5km')} available={!!mdcById.evac_shelters_5km?.available} />
              <MdcCell label={t('Cooling centers (3km)','Centros frescos (3km)')} value={mdcVal('cooling_centers_3km','count_3km')} available={!!mdcById.cooling_centers_3km?.available} />
            </div>
          </div>

          {/* Group 4: Walkability / amenities / neighborhood */}
          <div className="mt-5">
            <div className="mono text-[9px] uppercase tracking-widest2 text-white/40 mb-2">— {t('WALKABILITY · AMENITIES · NEIGHBORHOOD','CAMINABILIDAD · SERVICIOS · VECINDARIO')}</div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mono text-[11px]">
              <MdcCell label={t('Bus stops (400m)','Paradas bus (400m)')} value={mdcVal('bus_stops_400m','count_400m')} available={!!mdcById.bus_stops_400m?.available} highlight={mdcVal('bus_stops_400m','walkable_transit')} />
              <MdcCell label={t('Parks (1km)','Parques (1km)')} value={mdcVal('parks_1km','count_1km')} available={!!mdcById.parks_1km?.available} />
              <MdcCell label={t('Libraries (3km)','Bibliotecas (3km)')} value={mdcVal('libraries_3km','count_3km')} available={!!mdcById.libraries_3km?.available} />
              <MdcCell label={t('311 (500m)','311 (500m)')} value={mdcVal('311_recent','count_500m')} available={!!mdcById['311_recent']?.available} highlight={(mdcVal('311_recent','count_500m') || 0) >= 20} />
              <MdcCell label={t('Capital projects (1.5km)','Proyectos (1.5km)')} value={mdcVal('implementation_projects','count_1_5km')} available={!!mdcById.implementation_projects?.available} highlight={(mdcVal('implementation_projects','count_1_5km') || 0) >= 3} />
              <MdcCell label={t('Aviation 65+ dB','Aviación 65+ dB')} value={mdcVal('aviation_noise_65db','in_65db_zone') ? t('yes','sí') : t('no','no')} available={!!mdcById.aviation_noise_65db?.available} highlight={mdcVal('aviation_noise_65db','in_65db_zone')} />
              <MdcCell label={t('Aviation 75+ dB','Aviación 75+ dB')} value={mdcVal('aviation_noise_75db','in_75db_zone') ? t('yes','sí') : t('no','no')} available={!!mdcById.aviation_noise_75db?.available} highlight={mdcVal('aviation_noise_75db','in_75db_zone')} />
            </div>
          </div>

          <div className="mt-5 mono text-[10px] text-white/40">source · miami_dade_arcgis · {mdc.layer_catalog_count} total layers in catalog · queried {mdc.layers_queried}</div>
        </div>
      )}

      {/* Broward County ArcGIS (Fort Lauderdale primary host) */}
      {bwd.ok && bwd.applicable && (
        <div className="mt-6 border-t hairline pt-6" data-testid="bwd-arcgis-panel">
          <div className="flex items-baseline justify-between gap-3 flex-wrap">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ BROWARD (FTL) ARCGIS · {bwd.layers_queried} curated layers</div>
            <div className="mono text-[10px] text-white/50">{bwd.counts?.available}/{bwd.counts?.total} responded · host {bwd.primary_host}</div>
          </div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mono text-[11px]">
            {(bwd.indicators || []).map(ind => {
              const s = ind.available ? (ind.summary || {}) : {};
              const primary = Object.entries(s).find(([k]) => k !== 'attrs' && k !== 'sample_types');
              const value = primary ? String(primary[1] === null || primary[1] === undefined ? '—' : primary[1]) : '—';
              const highlight = ['AE','A','V','VE'].includes(s.zone) || ['AE','A','V','VE'].includes(s.prelim_zone) || s.in_action_area === true || ['A','B'].includes(s.zone);
              return (
                <MdcCell key={ind.id} label={ind.label} value={value} available={!!ind.available} highlight={highlight} />
              );
            })}
          </div>
          <div className="mt-4 mono text-[10px] text-white/40">source · broward_ftl_arcgis · adapter v{bwd.adapter_version}</div>
        </div>
      )}

      {/* Palm Beach — honest disclosure */}
      {pbc.applicable && (
        <div className="mt-6 border-t hairline pt-6" data-testid="pbc-arcgis-panel">
          <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ PALM BEACH ARCGIS</div>
          <div className="mt-3 border border-amber-400/40 bg-amber-400/5 p-3 mono text-[11px] text-amber-200">
            <span className="text-amber-400 uppercase tracking-widest2">⚠ not_available</span> — {pbc.reason}. {pbc.note}
          </div>
        </div>
      )}
    </div>
  );
}

function NegotiationScriptCard({ script, lang }) {
  if (!script) return null;
  const summary = lang === 'es' ? (script.summary_es || script.summary_en) : (script.summary_en || script.summary_es);
  const anchors = script.anchors || [];
  if (!summary && anchors.length === 0) return null;
  return (
    <div className="lg:col-span-12 border hairline bg-surface1 p-6" data-testid="negotiation-script">
      <div className="mono text-[10px] uppercase tracking-widest2 text-gold">§ NEGOTIATION SCRIPT</div>
      {summary && <p className="mt-4 text-white/80 leading-relaxed max-w-4xl">{summary}</p>}
      {anchors.length > 0 && (
        <div className="mt-6 space-y-4">
          {anchors.map((a) => (
            <div key={a.anomaly + (a.script_line_en || '').slice(0,20)} className="border-l-2 border-gold pl-4 py-2">
              <div className="flex items-baseline justify-between gap-4 flex-wrap">
                <div className="mono text-[11px] uppercase tracking-widest2 text-white/80">{a.anomaly}</div>
                {a.estimated_price_impact_pct != null && (
                  <div className="mono text-[10px] uppercase tracking-widest2 text-gold">
                    ~ −{a.estimated_price_impact_pct}% price impact
                  </div>
                )}
              </div>
              {a.leverage && <div className="text-sm text-white/60 mt-1">{a.leverage}</div>}
              <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                {a.script_line_en && (
                  <div className="border hairline bg-obsidian p-3">
                    <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">EN</div>
                    <div className="serif italic text-white/90 mt-1 text-sm leading-relaxed">"{a.script_line_en}"</div>
                  </div>
                )}
                {a.script_line_es && (
                  <div className="border hairline bg-obsidian p-3">
                    <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">ES</div>
                    <div className="serif italic text-white/90 mt-1 text-sm leading-relaxed">"{a.script_line_es}"</div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReportBody({ data }) {
  const r = data.report || {};
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
      {/* Header */}
      <div className="lg:col-span-12 border hairline bg-surface1 p-8">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold">DEAL DECISION REPORT</div>
        <div className="flex flex-wrap items-start justify-between gap-6 mt-4">
          <div>
            <h1 className="serif text-4xl md:text-5xl leading-tight text-white">{data.address}</h1>
            <div className="mono text-xs text-white/50 mt-2 flex gap-4 flex-wrap">
              {data.folio && <span>Folio · {data.folio}</span>}
              {data.asking_price != null && <span>Asking · {fmtMoney(data.asking_price)}</span>}
              {data.intended_use && <span>Use · {data.intended_use.replace('_',' ')}</span>}
              {data.created_at && <span>Issued · {new Date(data.created_at).toLocaleDateString()}</span>}
            </div>
          </div>
          <VerdictBadge v={r.verdict} conf={r.verdict_confidence} />
        </div>
        {r.verdict_headline && <div className="serif italic text-2xl text-white/90 mt-6 max-w-4xl">"{r.verdict_headline}"</div>}
      </div>

      {/* Executive Summary */}
      {r.executive_summary && (
        <div className="lg:col-span-8 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">EXECUTIVE SUMMARY</div>
          <p className="mt-4 text-white/80 leading-relaxed">{r.executive_summary}</p>
        </div>
      )}

      {/* AVM */}
      {r.avm_range && (
        <div className="lg:col-span-4 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">AVM EVIDENCE RANGE</div>
          <div className="mt-4">
            <AVMBar low={r.avm_range.low} median={r.avm_range.median} high={r.avm_range.high} />
          </div>
          <div className="mt-4 mono text-[10px] uppercase tracking-widest2 text-white/40">confidence · <span className="text-white">{r.avm_range.confidence}</span>/100</div>
          {r.avm_range.method_note && <div className="mt-3 text-xs text-white/50 italic">{r.avm_range.method_note}</div>}
        </div>
      )}

      {/* Comps */}
      {r.comps_analysis && (
        <div className="lg:col-span-12 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">COMPARABLES ANALYSIS</div>
          <p className="mt-4 text-white/80">{r.comps_analysis.summary}</p>
          {r.comps_analysis.verification_required?.length > 0 && (
            <div className="mt-6">
              <div className="mono text-[10px] uppercase tracking-widest2 text-gold">VERIFICATION REQUIRED</div>
              <ul className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-white/70">
                {r.comps_analysis.verification_required.map((v, i) => (
                  <li key={i} className="flex gap-3"><span className="text-gold mono">▸</span>{v}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Risk Stack */}
      {r.risk_stack?.length > 0 && (
        <div className="lg:col-span-8 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">RISK STACK</div>
          <div className="mt-4 space-y-3">
            {r.risk_stack.map((rk, i) => (
              <div key={i} className={`border-l-2 pl-4 py-2 ${rk.level === 'high' ? 'border-red-400' : rk.level === 'moderate' ? 'border-amber-400' : rk.level === 'low' ? 'border-emerald-400' : 'border-white/20'}`}>
                <div className="flex items-center justify-between">
                  <div className="mono text-[11px] uppercase tracking-widest2 text-white/70">{rk.category}</div>
                  <div className="flex items-center gap-2">
                    <RiskLevelBadge level={rk.level} />
                    <span className="mono text-[10px] text-white/40">conf {rk.confidence}</span>
                  </div>
                </div>
                <div className="text-sm text-white/80 mt-1">{rk.description}</div>
                {rk.verification && <div className="mt-1 mono text-xs text-white/50">verify: {rk.verification}</div>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Red Flags & Levers */}
      <div className="lg:col-span-4 flex flex-col gap-4">
        {r.red_flags?.length > 0 && (
          <div className="border hairline bg-surface1 p-6">
            <div className="mono text-[10px] uppercase tracking-widest2 text-red-400">RED FLAGS</div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/80">
              {r.red_flags.map((f, i) => <li key={i} className="flex gap-3"><span className="text-red-400 mono mt-1">◆</span>{f}</li>)}
            </ul>
          </div>
        )}
        {r.negotiation_levers?.length > 0 && (
          <div className="border hairline bg-surface1 p-6">
            <div className="mono text-[10px] uppercase tracking-widest2 text-gold">NEGOTIATION LEVERS</div>
            <ul className="mt-4 space-y-2.5 text-sm text-white/80">
              {r.negotiation_levers.map((l, i) => <li key={i} className="flex gap-3"><span className="text-gold mono mt-1">▸</span>{l}</li>)}
            </ul>
          </div>
        )}
      </div>

      {/* Source Ledger */}
      {r.source_ledger?.length > 0 && (
        <div className="lg:col-span-12 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-white/40">SOURCE LEDGER</div>
          <div className="mt-4 overflow-x-auto">
            <table className="w-full mono text-xs">
              <thead>
                <tr className="text-white/40 text-[10px] uppercase tracking-widest2 border-b hairline">
                  <th className="text-left py-2 pr-3">Field</th>
                  <th className="text-left py-2 pr-3">Value</th>
                  <th className="text-left py-2 pr-3">Source</th>
                  <th className="text-right py-2 pr-3">Conf.</th>
                  <th className="text-right py-2">Missingness</th>
                </tr>
              </thead>
              <tbody className="text-white/80">
                {r.source_ledger.map((s, i) => (
                  <tr key={i} className="border-b hairline last:border-0">
                    <td className="py-2 pr-3 text-white">{s.field}</td>
                    <td className="py-2 pr-3">{s.value}</td>
                    <td className="py-2 pr-3 text-white/50">{s.source}</td>
                    <td className="py-2 pr-3 text-right"><span className={s.confidence >= 70 ? 'text-emerald-400' : s.confidence >= 40 ? 'text-amber-400' : 'text-red-400'}>{s.confidence}</span></td>
                    <td className="py-2 text-right">
                      <span className={`inline-block px-1.5 py-0.5 text-[10px] uppercase ${s.missingness === 'none' ? 'text-emerald-400 border border-emerald-400/40' : s.missingness === 'partial' ? 'text-amber-400 border border-amber-400/40' : 'text-red-400 border border-red-400/40'}`}>
                        {s.missingness}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Spatial Forensics — best-effort integrations (USGS Elev + Overpass + NOAA) */}
      <SpatialForensicsCard spatial={data.enrichments?.spatial} lang={data.lang || 'en'} />

      {/* Negotiation Script — Claude-generated bilingual */}
      <NegotiationScriptCard script={r.negotiation_script} lang={data.lang || 'en'} />

      {/* Checklists */}
      <div className="lg:col-span-6 border hairline bg-surface1 p-6">
        <div className="mono text-[10px] uppercase tracking-widest2 text-gold">OFFER CHECKLIST</div>
        <ul className="mt-4 space-y-2 text-sm text-white/80">
          {(r.offer_checklist || []).map((c, i) => (
            <li key={i} className="flex gap-3"><span className="text-gold mono">☐</span>{c}</li>
          ))}
        </ul>
      </div>
      <div className="lg:col-span-6 border hairline bg-surface1 p-6">
        <div className="mono text-[10px] uppercase tracking-widest2 text-white/60">VERIFICATION QUESTIONS</div>
        <ul className="mt-4 space-y-2 text-sm text-white/80">
          {(r.verification_questions || []).map((c, i) => (
            <li key={i} className="flex gap-3"><span className="text-white/40 mono">?</span>{c}</li>
          ))}
        </ul>
      </div>

      {/* Disclaimer */}
      <div className="lg:col-span-12 border hairline bg-obsidian p-4 mono text-[10px] uppercase tracking-widest2 text-white/50">
        {r.disclaimer || 'Not an appraisal. Not investment advice. Model-assisted due diligence.'}
      </div>

      {/* Enrichments block (visible when live sources were used) */}
      {data.enrichments?.fema && !data.enrichments.fema.error && (
        <div className="lg:col-span-12 border hairline bg-surface1 p-6">
          <div className="mono text-[10px] uppercase tracking-widest2 text-emerald-400">LIVE PUBLIC DATA — FEMA National Flood Hazard Layer</div>
          <div className="mt-4 grid grid-cols-2 md:grid-cols-5 gap-3 mono text-xs">
            <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Zone</div><div className="text-gold text-lg mt-1">{data.enrichments.fema.zone}</div></div>
            <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Subtype</div><div className="text-white mt-1">{data.enrichments.fema.zone_subtype || '—'}</div></div>
            <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">BFE</div><div className="text-white mt-1">{data.enrichments.fema.bfe != null ? `${data.enrichments.fema.bfe} ft` : '—'}</div></div>
            <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">FIRM Panel</div><div className="text-white mt-1">{data.enrichments.fema.firm_panel || '—'}</div></div>
            <div><div className="text-white/40 text-[10px] uppercase tracking-widest2">Confidence</div><div className="text-emerald-400 mt-1">{data.enrichments.fema.confidence}</div></div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function ReportViewer() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const shareToken = sp.get('t');
  const adminToken = typeof window !== 'undefined' ? localStorage.getItem('pythia_admin_token') : null;

  useEffect(() => {
    const endpoint = shareToken
      ? api.get(`/reports/${id}/shared`, { params: { t: shareToken } })
      : api.get(`/reports/${id}`);
    endpoint.then(r => setData(r.data)).catch(e => setErr(e?.response?.data?.detail || 'Not found'));
  }, [id, shareToken]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/admin/reports/${id}/pdf`, {
        headers: { 'X-Admin-Token': adminToken },
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `pythia_${(data?.address || 'report').replace(/[^a-z0-9]/gi, '_').slice(0, 40)}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('PDF download failed:', err);
      // responseType:'blob' wraps error JSON in a Blob — decode it so we can surface `detail`.
      let detail = null;
      try {
        const blob = err?.response?.data;
        if (blob && typeof blob.text === 'function') {
          const txt = await blob.text();
          detail = txt ? JSON.parse(txt).detail : null;
        }
      } catch {
        detail = null;
      }
      alert(detail || err?.response?.statusText || 'Download failed');
    }
  };

  const copyShareLink = async () => {
    if (!data?.share_token) return;
    const url = `${window.location.origin}/report/${id}?t=${data.share_token}`;
    try {
      await navigator.clipboard.writeText(url);
      alert('Share link copied');
    } catch (err) {
      // Clipboard permission denied — fall back to prompt so user can copy manually
      console.warn('Clipboard write blocked, falling back to prompt:', err);
      window.prompt('Copy this share link:', url);
    }
  };

  if (err) return (
    <div>
      <Nav />
      <div className="max-w-2xl mx-auto py-24 px-6 text-center">
        <div className="mono text-[10px] uppercase tracking-widest2 text-red-400">Error</div>
        <h1 className="serif text-4xl mt-4">{err}</h1>
      </div>
      <Footer />
    </div>
  );
  if (!data) return (
    <div>
      <Nav />
      <div className="max-w-2xl mx-auto py-24 px-6 text-center mono text-xs text-white/50">Loading report<span className="blink">_</span></div>
    </div>
  );

  return (
    <div>
      <Nav />
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-10" data-testid="report-viewer">
        {adminToken && (
          <div className="flex justify-end gap-3 mb-4">
            {data.share_token && (
              <button onClick={copyShareLink} className="mono text-[10px] uppercase tracking-widest2 border hairline px-4 py-2 hover:border-gold hover:text-gold" style={{transitionProperty: 'color, border-color'}} data-testid="report-share-btn">
                ⇢ Copy Share Link
              </button>
            )}
            <button onClick={downloadPdf} className="mono text-[10px] uppercase tracking-widest2 border border-gold text-gold px-4 py-2 hover:bg-gold hover:text-obsidian" style={{transitionProperty: 'color, background-color'}} data-testid="report-pdf-btn">
              ↓ Download PDF
            </button>
          </div>
        )}
        <ReportBody data={data} />
        <div className="mt-6 grid grid-cols-1 lg:grid-cols-12 gap-4">
          <ReportQA
            reportId={id}
            shareToken={shareToken}
            isAdmin={!!adminToken}
            lang={data.lang || 'en'}
          />
        </div>
      </div>
      <DisclaimerBar />
      <Footer />
    </div>
  );
}
